// =============================================================================
// Origin UI — src/pages/admin/LetterTemplateAdminTab.tsx
// Admin Config sub-tab for managing letter templates.
// List all templates, edit metadata, upload .docx files, toggle active/inactive.
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { letterApi, LetterTemplateListItem, LetterTemplateDetail } from '@/services/letterApi';
import { Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './admin.module.css';

export function LetterTemplateAdminTab() {
  const [templates, setTemplates] = useState<LetterTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDetail, setEditDetail] = useState<LetterTemplateDetail | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(getEmptyForm());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { load(); }, []);

  function getEmptyForm() {
    return {
      code: '', name: '', description: '', jurisdiction: 'All',
      templateBody: '', letterheadType: 'Dubai', useUniversalFooter: false,
      includeStamp: true, includeSalary: false, compensationOnly: false,
      requiresAdditionalInput: false, additionalInputSchema: '',
      signatoryDefault: '', signatoryTitleDefault: '', sortOrder: 99,
    };
  }

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data = await letterApi.getAllTemplates();
      setTemplates(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  async function startEdit(templateId: string) {
    try {
      setError('');
      const detail = await letterApi.getAdminTemplateDetail(templateId);
      setEditingId(templateId);
      setEditDetail(detail);
      setCreating(false);
      setSuccess('');
    } catch (e: any) {
      setError(e.message || 'Failed to load template detail');
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDetail(null);
  }

  async function saveEdit() {
    if (!editDetail || !editingId) return;
    try {
      setSaving(true);
      setError('');
      await letterApi.updateTemplate(editingId, {
        code: editDetail.code,
        name: editDetail.name,
        description: editDetail.description,
        jurisdiction: editDetail.jurisdiction,
        templateBody: editDetail.templateBody,
        letterheadType: editDetail.letterheadType,
        useUniversalFooter: editDetail.useUniversalFooter,
        includeStamp: editDetail.includeStamp,
        includeSalary: editDetail.includeSalary,
        compensationOnly: editDetail.compensationOnly,
        requiresAdditionalInput: editDetail.requiresAdditionalInput,
        additionalInputSchema: editDetail.additionalInputSchema,
        signatoryDefault: editDetail.signatoryDefault,
        signatoryTitleDefault: editDetail.signatoryTitleDefault,
        sortOrder: editDetail.sortOrder,
      });
      cancelEdit();
      setSuccess('Template saved successfully.');
      setTimeout(() => setSuccess(''), 3000);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(templateId: string) {
    try {
      setError('');
      await letterApi.toggleTemplate(templateId);
      setSuccess('Template status toggled.');
      setTimeout(() => setSuccess(''), 2000);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to toggle');
    }
  }

  async function handleFileUpload(templateId: string, file: File) {
    try {
      setUploading(true);
      setError('');
      const token = await (await import('@/services/apiService')).api.getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/letter-templates/${templateId}/upload-docx`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || 'Upload failed');
      }

      setSuccess('Template .docx file uploaded successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleCreate() {
    if (!createForm.code || !createForm.name) {
      setError('Code and Name are required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const created = await letterApi.createTemplate({
        ...createForm,
        sortOrder: Number(createForm.sortOrder) || 99,
      } as any);

      // Upload .docx if provided
      const fileInput = createFileInputRef.current;
      if (fileInput?.files?.[0]) {
        await handleFileUpload(created.letterTemplateId, fileInput.files[0]);
      }

      setCreating(false);
      setCreateForm(getEmptyForm());
      setSuccess('Template created successfully.');
      setTimeout(() => setSuccess(''), 3000);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && (
        <div style={{ padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--green-light)', color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>
          {success}
        </div>
      )}

      <div className={styles.toolBar}>
        <span className={styles.count}>{templates.length} letter templates</span>
        <button className={styles.addBtn} onClick={() => { setCreating(true); setEditingId(null); setSuccess(''); }}>
          + New Template
        </button>
      </div>

      <p className={styles.muted} style={{ marginBottom: 16 }}>
        Letter templates define the document structure and placeholders for HR letter generation.
        Each template needs a .docx file in blob storage with &#123;Token&#125; placeholders.
        Click a template to edit its settings or upload a new .docx file.
      </p>

      {/* Create form */}
      {creating && (
        <Card className={styles.formCard} padding={true}>
          <h4 className={styles.formTitle}>New Letter Template</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Code *" value={createForm.code} onChange={v => setCreateForm({ ...createForm, code: v })} placeholder="e.g. CONF_EMP" />
            <Field label="Name *" value={createForm.name} onChange={v => setCreateForm({ ...createForm, name: v })} placeholder="Confirmation of Employment" />
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Description" value={createForm.description} onChange={v => setCreateForm({ ...createForm, description: v })} placeholder="When to use this template" />
            </div>
            <Field label="Jurisdiction" value={createForm.jurisdiction} onChange={v => setCreateForm({ ...createForm, jurisdiction: v })} placeholder="All / Dubai / Oman / Singapore" />
            <Field label="Letterhead Type" value={createForm.letterheadType} onChange={v => setCreateForm({ ...createForm, letterheadType: v })} placeholder="Dubai / Oman / Singapore" />
            <Field label="Signatory Name" value={createForm.signatoryDefault} onChange={v => setCreateForm({ ...createForm, signatoryDefault: v })} placeholder="Shannon Naudé" />
            <Field label="Signatory Title" value={createForm.signatoryTitleDefault} onChange={v => setCreateForm({ ...createForm, signatoryTitleDefault: v })} placeholder="Head of Human Resources" />
            <Field label="Sort Order" value={String(createForm.sortOrder)} onChange={v => setCreateForm({ ...createForm, sortOrder: Number(v) || 0 })} placeholder="99" />
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
            <Checkbox label="Contains salary data (HR only)" checked={createForm.includeSalary} onChange={v => setCreateForm({ ...createForm, includeSalary: v })} />
            <Checkbox label="Compensation only (Shannon)" checked={createForm.compensationOnly} onChange={v => setCreateForm({ ...createForm, compensationOnly: v })} />
            <Checkbox label="Include stamp" checked={createForm.includeStamp} onChange={v => setCreateForm({ ...createForm, includeStamp: v })} />
            <Checkbox label="Universal footer (Dubai HQ)" checked={createForm.useUniversalFooter} onChange={v => setCreateForm({ ...createForm, useUniversalFooter: v })} />
            <Checkbox label="Requires additional input" checked={createForm.requiresAdditionalInput} onChange={v => setCreateForm({ ...createForm, requiresAdditionalInput: v })} />
          </div>
          {createForm.requiresAdditionalInput && (
            <div style={{ marginTop: 12 }}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Additional Input Schema (JSON)</label>
                <textarea
                  className={styles.fieldInput}
                  value={createForm.additionalInputSchema}
                  onChange={e => setCreateForm({ ...createForm, additionalInputSchema: e.target.value })}
                  rows={4}
                  placeholder={'[\n  { "key": "DestinationCountry", "label": "Destination country", "type": "text", "required": true }\n]'}
                  style={{ fontFamily: "'SF Mono', 'Menlo', monospace", fontSize: 12, resize: 'vertical' }}
                />
              </div>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Template .docx file</label>
              <input ref={createFileInputRef} type="file" accept=".docx" style={{ fontSize: 13 }} />
              <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                Upload a Word document with &#123;Token&#125; placeholders. File will be stored in blob as templates/CODE.docx
              </div>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setCreating(false); setCreateForm(getEmptyForm()); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </Card>
      )}

      {/* Template list */}
      {templates.map(t => (
        <div key={t.letterTemplateId} style={{ marginBottom: 12 }}>
          <Card className={styles.formCard} padding={true}>
            {/* Summary row */}
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
              onClick={() => editingId === t.letterTemplateId ? cancelEdit() : startEdit(t.letterTemplateId)}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>{t.name}</span>
                  <span className={styles.mono} style={{ fontSize: 11, color: 'var(--gray-400)' }}>{t.code}</span>
                </div>
                {t.description && <div style={{ color: 'var(--gray-500)', fontSize: 13, marginTop: 2 }}>{t.description}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  {t.jurisdiction && (
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#E3F5F6', color: '#00B0B9' }}>
                      {t.jurisdiction === 'All' ? 'All Offices' : t.jurisdiction}
                    </span>
                  )}
                  {t.includeSalary && (
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#FFF0E0', color: '#FF8300' }}>
                      Salary
                    </span>
                  )}
                  {t.compensationOnly && (
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#FFE0E0', color: '#CC3333' }}>
                      Compensation Only
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <button
                  onClick={e => { e.stopPropagation(); handleToggle(t.letterTemplateId); }}
                  style={{
                    padding: '4px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: 'none',
                    background: t.isActive ? 'var(--green-light)' : 'var(--gray-100)',
                    color: t.isActive ? 'var(--green)' : 'var(--gray-400)',
                  }}
                >
                  {t.isActive ? 'Active' : 'Inactive'}
                </button>
                <span style={{ color: 'var(--gray-400)', fontSize: 18 }}>
                  {editingId === t.letterTemplateId ? '▲' : '▼'}
                </span>
              </div>
            </div>

            {/* Edit form (expanded) */}
            {editingId === t.letterTemplateId && editDetail && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-200)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Code *" value={editDetail.code} onChange={v => setEditDetail({ ...editDetail, code: v })} />
                  <Field label="Name *" value={editDetail.name} onChange={v => setEditDetail({ ...editDetail, name: v })} />
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Field label="Description" value={editDetail.description || ''} onChange={v => setEditDetail({ ...editDetail, description: v })} />
                  </div>
                  <Field label="Jurisdiction" value={editDetail.jurisdiction || ''} onChange={v => setEditDetail({ ...editDetail, jurisdiction: v })} placeholder="All / Dubai / Oman / Singapore" />
                  <Field label="Letterhead Type" value={editDetail.letterheadType} onChange={v => setEditDetail({ ...editDetail, letterheadType: v })} />
                  <Field label="Signatory Name" value={editDetail.signatoryDefault || ''} onChange={v => setEditDetail({ ...editDetail, signatoryDefault: v })} />
                  <Field label="Signatory Title" value={editDetail.signatoryTitleDefault || ''} onChange={v => setEditDetail({ ...editDetail, signatoryTitleDefault: v })} />
                  <Field label="Sort Order" value={String(editDetail.sortOrder)} onChange={v => setEditDetail({ ...editDetail, sortOrder: Number(v) || 0 })} />
                </div>

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
                  <Checkbox label="Contains salary data (HR only)" checked={editDetail.includeSalary} onChange={v => setEditDetail({ ...editDetail, includeSalary: v })} />
                  <Checkbox label="Compensation only (Shannon)" checked={editDetail.compensationOnly} onChange={v => setEditDetail({ ...editDetail, compensationOnly: v })} />
                  <Checkbox label="Include stamp" checked={editDetail.includeStamp} onChange={v => setEditDetail({ ...editDetail, includeStamp: v })} />
                  <Checkbox label="Universal footer (Dubai HQ)" checked={editDetail.useUniversalFooter} onChange={v => setEditDetail({ ...editDetail, useUniversalFooter: v })} />
                  <Checkbox label="Requires additional input" checked={editDetail.requiresAdditionalInput} onChange={v => setEditDetail({ ...editDetail, requiresAdditionalInput: v })} />
                </div>

                {editDetail.requiresAdditionalInput && (
                  <div style={{ marginTop: 12 }}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Additional Input Schema (JSON)</label>
                      <textarea
                        className={styles.fieldInput}
                        value={editDetail.additionalInputSchema || ''}
                        onChange={e => setEditDetail({ ...editDetail, additionalInputSchema: e.target.value })}
                        rows={4}
                        style={{ fontFamily: "'SF Mono', 'Menlo', monospace", fontSize: 12, resize: 'vertical' }}
                      />
                    </div>
                  </div>
                )}

                {/* File upload */}
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--gray-100)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Template .docx File</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx"
                      style={{ fontSize: 13 }}
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (file) await handleFileUpload(t.letterTemplateId, file);
                      }}
                    />
                    {uploading && <span style={{ fontSize: 12, color: 'var(--orange)' }}>Uploading...</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                    Upload a replacement .docx with &#123;Token&#125; placeholders. Saved as templates/{editDetail.code}.docx in blob.
                  </div>
                </div>

                <div className={styles.formActions} style={{ marginTop: 16 }}>
                  <button className={styles.cancelBtn} onClick={cancelEdit}>Cancel</button>
                  <button className={styles.saveBtn} onClick={saveEdit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <input
        className={styles.fieldInput}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function Checkbox({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--navy)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
